/**
 * State machine for the Contact Us form (COM-735). UI-agnostic: owns the
 * submit, the double-submit guard, the post-success reset and the success
 * message's lifetime; the organism only renders `status`. Field values and
 * validation belong to RVF — this hook receives values that already passed.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { submitContactUs } from "./contact-us.client";
import {
  CONTACT_US_SUCCESS_VISIBLE_MS,
  statusForResult,
} from "./contact-us.logic";
import type {
  ContactUsStatus,
  ContactUsValues,
  UseContactUsFormOptions,
  UseContactUsFormResult,
} from "./types";

const IDLE: ContactUsStatus = { kind: "idle" };

export function useContactUsForm({
  enabled,
}: UseContactUsFormOptions): UseContactUsFormResult {
  const [status, setStatus] = useState<ContactUsStatus>(IDLE);
  const [formKey, setFormKey] = useState(0);
  // Same-tick double-click guard. `status` flips on the next commit, so a
  // second click in the same tick would otherwise see "idle" and post twice —
  // and each post is a real email to the support inbox (AC: no duplicates).
  const inFlight = useRef(false);

  // The live form's success line hides after 5s. Failures stay until the next
  // attempt: the shopper still has their values and needs the reason to retry.
  useEffect(() => {
    if (status.kind !== "succeeded") return;
    const timer = setTimeout(
      () => setStatus(IDLE),
      CONTACT_US_SUCCESS_VISIBLE_MS,
    );
    return () => clearTimeout(timer);
  }, [status]);

  const submit = useCallback(
    async (values: ContactUsValues) => {
      if (!enabled || inFlight.current) return;
      inFlight.current = true;
      setStatus({ kind: "submitting" });
      try {
        const result = await submitContactUs(values);
        // Clear the fields ONLY on success; a failure keeps everything the
        // shopper typed so a retry is one click (AC: values preserved).
        if (result.ok) setFormKey(key => key + 1);
        setStatus(statusForResult(result));
      } catch {
        // The client never throws by contract; this only guarantees a broken
        // contract can't strand the button in `submitting` forever.
        setStatus({ kind: "failed", reason: "unavailable" });
      } finally {
        inFlight.current = false;
      }
    },
    [enabled],
  );

  return { status, formKey, submit };
}
